import type { ClientHealth, ClientStatusDto } from '@citilight-monitor/contracts';
import { LayoutGrid, Rows3 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, EmptyState } from '../components/atoms';
import { SectionHeader, SegmentedControl, type SegmentOption } from '../components/molecules';
import { AlertActivity, ClientCards, ClientsTable, FleetHealthStats, IssueList } from '../components/organisms';
import { PageTemplate } from '../components/templates';
import { useFleetAlerts, useOverview } from '../lib/api';
import { fleetIssues, HEALTH_LABELS, HEALTH_ORDER, isServer } from '../lib/metrics';

type View = 'cards' | 'table';

const VIEW_OPTIONS: SegmentOption<View>[] = [
  { value: 'cards', ariaLabel: 'Show clients as cards', icon: <LayoutGrid size={15} aria-hidden /> },
  { value: 'table', ariaLabel: 'Show clients as a table', icon: <Rows3 size={15} aria-hidden /> },
];

export function OverviewPage() {
  const { data, error, isPending } = useOverview();
  const [healthFilter, setHealthFilter] = useState<ClientHealth | null>(null);
  const [view, setView] = useState<View>('cards');

  const ids = data?.clients.map((c) => c.client.id) ?? [];
  const fleetAlerts = useFleetAlerts(ids);

  if (isPending || error) {
    return (
      <PageTemplate title="Fleet overview">
        <Card raised>
          <EmptyState>{error ? `Could not load clients: ${error.message}` : 'Loading clients…'}</EmptyState>
        </Card>
      </PageTemplate>
    );
  }

  const pollIntervalMs = data.pollIntervalMinutes * 60_000;
  const issues = fleetIssues(data.clients, pollIntervalMs);
  const alerts = fleetAlerts.flatMap((q) => q.data ?? []);
  const shown = data.clients
    .filter((c) => !healthFilter || c.health === healthFilter)
    .sort((a, b) => HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health] || a.client.name.localeCompare(b.client.name));
  const servers = shown.filter((c) => isServer(c.client.metrics));
  const apps = shown.filter((c) => !isServer(c.client.metrics));

  return (
    <PageTemplate
      title="Fleet overview"
      meta={`${data.clients.length} clients · polled every ${data.pollIntervalMinutes} min`}
      hero={<FleetHealthStats clients={data.clients} filter={healthFilter} onFilter={setHealthFilter} />}
      actions={<SegmentedControl options={VIEW_OPTIONS} value={view} onChange={setView} ariaLabel="Client list layout" />}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
        <IssueList issues={issues} title="Needs attention" showClient allClear="Every client is within its limits." />
        <AlertActivity alerts={alerts} ready={fleetAlerts.every((a) => !a.isPending)} />
      </div>

      {healthFilter && (
        <p className="-mb-2 text-[13px] text-fg-muted">
          Showing {HEALTH_LABELS[healthFilter].label.toLowerCase()} only ·{' '}
          <button type="button" className="cursor-pointer font-medium text-accent hover:underline" onClick={() => setHealthFilter(null)}>
            Show all
          </button>
        </p>
      )}

      <ClientGroup title="Servers" description="CPU, memory and disk against their limits." clients={servers} view={view} link={{ to: '/servers', label: 'Compare servers' }} empty="No server matches this filter." />

      <ClientGroup title="Applications" description="Reachability and each app's own metrics." clients={apps} view={view} empty="No application matches this filter." />
    </PageTemplate>
  );
}

interface ClientGroupProps {
  title: string;
  description: string;
  clients: ClientStatusDto[];
  view: View;
  link?: { to: string; label: string };
  empty: string;
}

function ClientGroup({ title, description, clients, view, link, empty }: ClientGroupProps) {
  return (
    <section className="flex flex-col gap-4" aria-label={title}>
      <SectionHeader
        title={title}
        count={clients.length}
        description={description}
        actions={
          link && (
            <Link to={link.to} className="text-sm font-medium text-accent no-underline hover:underline">
              {link.label} →
            </Link>
          )
        }
      />
      {clients.length === 0 ? (
        <Card>
          <EmptyState>{empty}</EmptyState>
        </Card>
      ) : view === 'cards' ? (
        <ClientCards clients={clients} />
      ) : (
        <Card className="overflow-hidden">
          <ClientsTable clients={clients} />
        </Card>
      )}
    </section>
  );
}
